import classnames from 'classnames';
import React, { useState, useRef } from 'react';
import { Icon } from '../../Icon/Icon';
import { IconName } from '../../Icon/IconName';
import './../input.scss';
import { t } from '../../../locales/translate';
import { Spinner } from '../../Spinner/Spinner';
import { presence } from '../../../util/util';
import { FormField } from '../../../model/form';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';
import { useId } from '../../../util/react-util';

export interface TextFieldBase extends FormField<string | null> {
  icon?: IconName;
  actions?: React.ReactNode | React.ReactNodeArray;
  autoFocus?: boolean;
  displayOptional?: boolean;
  loading?: boolean;
}

export interface Props extends TextFieldBase {
  type?: 'text' | 'password' | 'email' | 'number' | 'custom-time';
  maxLength?: number;
  textFieldRef?: React.MutableRefObject<HTMLInputElement | null>;
  pattern?: string;
  formatter?: <T>(argument: T) => string | undefined;
}

export function FormTextField({
  name,
  value,
  label,
  icon,
  actions,
  description,
  placeholder,
  type,
  autoFocus,
  required,
  disabled,
  maxLength,
  readOnly,
  className,
  textFieldRef,
  pattern,
  displayOptional = true,
  loading,
  nullIfEmpty = false,
  inputId,
  errors,
  formatter,
  onChange,
  onBlur,
  touched,
  valid,
  onFocus,
}: Props): JSX.Element {
  const [active, setActive] = useState(false);
  const formattedValue = formatter ? formatter(value) : value === null ? undefined : value;
  const displayErrors = touched && !valid;
  const full = active || placeholder || !!value?.length;
  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);
  const id = useId(inputId);

  return (
    <div className={classnames('input', { 'input--with-icon': icon }, { 'input--disabled': disabled }, className)}>
      {icon && <Icon name={icon} className="input__icon" />}
      <div
        className={classnames('input__container', {
          'input__container--active': active,
          'input__container--full': full,
          'input__container--error': displayErrors,
        })}
      >
        <div className="input__input-wrapper">
          {label && (
            <label className="input__label" htmlFor={id}>
              {label}
              {displayOptional && !required && !disabled && ` (${t.general.optional})`}
            </label>
          )}
          <input
            id={id}
            name={name}
            className="input__input"
            value={formattedValue !== undefined ? formattedValue : ''}
            type={type || 'text'}
            onChange={(v) => {
              if (readOnly) {
                v.preventDefault();
                return;
              }
              const newValue = nullIfEmpty ? (presence(v.target.value) ?? null) : v.target.value;
              onChange?.(newValue);
            }}
            onFocus={() => {
              setActive(true);
              onFocus?.();
            }}
            onBlur={() => {
              setActive(false);
              onBlur?.();
            }}
            autoFocus={autoFocus}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            maxLength={maxLength}
            ref={(ref) => {
              inputRef.current = ref;
              if (textFieldRef) {
                textFieldRef.current = ref;
              }
            }}
            pattern={
              pattern !== undefined ? pattern : type === 'custom-time' ? '([01]?[0-9]|2[0-3]):[0-5][0-9]' : undefined
            }
          />
          {actions && <div className="input__actions">{actions}</div>}
          {displayErrors && <Icon name="alert-circle--sm" className="input__alert-circle" />}
          {loading && <Spinner className="input__loading" color="var(--color-text-label)" scale="0.6" />}
        </div>
        <div className="input__description-wrapper">
          {description && <div className="input__description">{description}</div>}
          <Collapse open={displayErrors}>
            {errors?.map((error) => {
              return (
                <div className="input__error-message" key={error}>
                  {t.errors[error] || error}
                </div>
              );
            })}
          </Collapse>
        </div>
      </div>
    </div>
  );
}
